/** @type {import('tailwindcss').Config} */
export default {
  content: ["./index.html", "./src/**/*.{js,ts,jsx,tsx}", "./lib/**/*.{js,ts,jsx,tsx}"],
  theme: {
    extend: {
      colors: {
        content: {
          base: "#FFF",
          soft: "#958ED8",
          disabled: "#59549F",
        },
        informative: {
          base: "#23B4C9",
          50: "#ECFEFF",
          100: "#CFFAFE",
          200: "#A5F3FC",
          300: "#67E8F9",
          400: "#22D3EE",
          500: "#06B6D4",
          600: "#0891B2",
          700: "#0E7490",
          800: "#155E75",
          900: "#164E63",
          950: "#083344",
        },
        warning: {
          base: "#D67D39",
          soft: "#A65028",
          soft2: "#E8BC89",
          strong: "#E8BC89",
          bold: "#F9EDDB",
          subtle: "#6C3722",
          50: "#FCF7F0",
          100: "#F9EDDB",
          200: "#F1D7B7",
          300: "#E8BC89",
          400: "#E09F64",
          500: "#D67D39",
          600: "#C8672E",
          700: "#A65028",
          800: "#854027",
          900: "#6C3722",
          950: "#3A1A10",
        },
        error: {
          base: "#EF4444",
          soft: "#B91C1C",
          strong: "#FCA5A5",
          bold: "#FEE2E2",
          subtle: "#7F1D1D",
          50: "#FEF2F2",
          100: "#FEE2E2",
          200: "#FECACA",
          300: "#FCA5A5",
          400: "#F87171",
          500: "#EF4444",
          600: "#DC2626",
          700: "#B91C1C",
          800: "#991B1B",
          900: "#7F1D1D",
          950: "#450A0A",
        },
        success: {
          base: "#15A462",
          soft: "#14814F",
          strong: "#85F0BE",
          bold: "#DCFCEC",
          subtle: "#135438",
          50: "#F0FDF6",
          100: "#DCFCEC",
          200: "#BAF8DA",
          300: "#85F0BE",
          400: "#49DF99",
          500: "#21C97B",
          600: "#15A462",
          700: "#14814F",
          800: "#156642",
          900: "#135438",
          950: "#052E1D",
        },
        darkBrand: {
          50: "#EBE9FF",
          100: "#CCCDFF",
          200: "#B3ACEF",
          300: "#958ED8",
          400: "#8E89D1",
          500: "#59549F",
          600: "#45408D",
          700: "#312D6F",
          800: "#231F55",
          900: "#151240",
          950: "#0C0629",
        },
        accentBrand: {
          50: "#F3F3FF",
          100: "#EAE9FE",
          200: "#D5D5FF",
          300: "#B7B4FE",
          400: "#9389FC",
          500: "#7059F9",
          600: "#562EF0",
          700: "#4E24DD",
          800: "#411EB9",
          900: "#1F0E67",
          950: "#371B97",
        },
        brand: {
          50: "#FAF5FF",
          100: "#F2E8FF",
          200: "#E7D5FF",
          300: "#D4B3FF",
          400: "#BA83FD",
          500: "#9D4EF8",
          600: "#8A32EB",
          700: "#7621CF",
          800: "#6420A9",
          900: "#531B88",
          950: "#360665",
        },
        stroke: {
          base: "#312D6F",
          soft: "#231F55",
          strong: "#59549F",
          error: "#EF4444",
        },
        commonAlpha: {
          base: "rgba(255, 255, 255, 0.4)",
          soft: "rgba(255, 255, 255, 0.1)",
          softer: "rgba(255, 255, 255, 0.04)",
          inverseBase: "rgba(0, 0, 0, 0.4)",
          inverseStrong: "rgba(0, 0, 0, 0.6)",
        },
        // components
        referBalls: {
          basket1BgStart: "#FBBF24",
          basket1BgStop: "#FBBF24",
          basket1Label: "#FBBF24",
          basket2BgStart: "#E09F64",
          basket2BgStop: "#E09F64",
          basket2Label: "#E09F64",
          basket3BgStart: "#8A32EB",
          basket3BgStop: "#8A32EB",
          basket3Label: "#BA83FD",
          basket4BgStart: "#00BCFF",
          basket4BgStop: "#00BCFF",
          basket4Label: "#00BCFF",
          basket5BgStart: "#59549F",
          basket5BgStop: "#59549F",
          basket5Label: "#B8AEDA",
          basketNormalBgStart: "#45408D",
          basketNormalBgStop: "#45408D",
          basketNormalLabel: "#8E89D1",
        },
      },
      backgroundImage: {
        header: "radial-gradient(133.74% 100.44% at 100% 50%, #231F55 0%, #151240 100%)",
        dialog: "radial-gradient(51.67% 51.19% at 51.67% 0%, #312D6F 0%, #151240 100%)",
        homeBtn: "linear-gradient(180deg, #312D6F 0%, #231F55 50.48%, #312D6F 100%)",
        grdPrimary: "radial-gradient(50% 100% at 50% 0%, #7059F9 0%, #4E24DD 100%)",
        grdPrimaryAlt: "linear-gradient(180deg, #371B97 0%, #411EB9 100%)",
        grdSecondary: "radial-gradient(50% 100% at 50% 0%, #312D6F 0%, #231F55 100%)",
        grdSecondaryAlt: "linear-gradient(180deg, #312D6F 0%, #45408D 100%)",
        bottomMenu: "radial-gradient(33.48% 103.75% at 52.2% 0%, #531B88 0%, #151240 100%)",
        bottomNavBtnRound: "linear-gradient(180deg,#9C40FF 0%,#3C39A8 100%)",
        bottomNavBtnRoundRimLight:
          "radial-gradient(46.67% 23.33% at 50% 100%, rgba(114, 187, 255, 0.80) 0%, rgba(75, 69, 234, 0.00) 100%)",
        bottomNavBtnIndicatorActive:
          "radial-gradient(44.58%_50%_at_50%_100%,rgba(109,93,255,0.60)_0%,rgba(42,28,91,0.00)_100%)",
        bottomNavBtnIndicator: "",
        pageTitle: "linear-gradient(180deg, #FFF 19.11%, #B3ACEF 71.43%)",
        gradientTextBase: "linear-gradient(180deg, #FFF 19.11%, #B3ACEF 71.43%)",
        tabNavSecondary: "linear-gradient(180deg,#312D6F 0%,#231F55 100%)",
        referFriendTopSection:
          "radial-gradient(73.33% 38.02% at 73.33% 58.07%, #371b9799 0%, rgba(0, 0, 0, 0.00) 100%)",
        progressBarTray: "linear-gradient(180deg, #151240 0%, #231F55 100%)",
        timerProgressBar: "linear-gradient(90deg, #7621CF 0%, #F59E0B 70%, #FCD34D 100%)",
        timerProgressStripes:
          "linear-gradient(-45deg,rgba(255,255,255,.15) 25%,transparent 25%,transparent 50%,rgba(255,255,255,.15) 50%,rgba(255,255,255,.15) 75%,transparent 75%,transparent)",
        card: "radial-gradient(46.81% 58.96% at 50% 0%,#333230 0%,#151240 100%)",
        flipClock: "linear-gradient(180deg, #45408D 0%, #312D6F 42%, #231F55 46.5%, #151240 100%)",
        grdHigh:
          "radial-gradient(71.88% 55.56% at 50% 0%, theme('backgroundColor.gradientTopHigh') 0%, theme('backgroundColor.gradientBottomHigh') 100%)",
        grdMid:
          "radial-gradient(69.19% 53.57% at 50% 0%, theme('backgroundColor.gradientTopMid') 0%, theme('backgroundColor.gradientBottomMid') 100%)",
        grdLow:
          "radial-gradient(69.19% 53.57% at 50% 0%, theme('backgroundColor.gradientTopLow') 0%, theme('backgroundColor.gradientBottomLow') 100%)",
      },
      backgroundColor: {
        backdrop: "#0C0629D9",
        overlay: "#000000CC",

        solidLow: "#141129",
        gradientTopLow: "#1F0E67",
        gradientBottomLow: "#141129",

        solidMid: "#1C1440",
        gradientTopMid: "#32276D",
        gradientBottomMid: "#1A1536",

        solidHigh: "#32276D",
        gradientTopHigh: "#32276D",
        gradientBottomHigh: "#21174C",
      },
    },
  },
  plugins: [import("@tailwindcss/typography")],
};
